import React, { useRef, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    IconButton,
    Button,
    Avatar
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PushPinIcon from '@mui/icons-material/PushPin';
import LinkIcon from '@mui/icons-material/Link';
import { useSharedStyles } from '../style';
import { DiscussionItem, DiscussionReply } from '../types';
import ReactQuill from 'react-quill'; // Import React-Quill
import 'react-quill/dist/quill.snow.css'; // Import styles
import {
    getDiscussionPosts,
    addForumDiscussionPost,
    lockToggleDiscussion,
    pinToggleDiscussion,
    starToggleDiscussion
} from '../../../../../../../Utils/apiService';
import { getUserDetails } from '../../../../../../../storageService';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
dayjs.extend(relativeTime);

interface DiscussionRightPanelProps {
    selectedDiscussion: DiscussionItem | undefined;
    selectedDiscussionId: string | null;
    discussionId: string | null;
    onNewDiscussion: () => void;
    replyText: string;
    onReplyTextChange: (text: string) => void;
    onSubmitReply: () => void;
    forumId?: string | number;
    onSnackbarOpen?: (severity: 'success' | 'error', message: string) => void;
    onPostCountChange: (count: number) => void;
}

const useStyles = makeStyles(() => ({
    rightPanel: {
        width: '70%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #eaeaea',
    },
    discussionHeader: {
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        textAlign: 'left',
    },
    discussionTitle: {
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
    },
    activeChip: {
        backgroundColor: '#e6f5ec',
        color: '#2e7d32',
        marginLeft: '10px',
        fontSize: '12px',
        height: '24px',
    },
    iconButtonGroup: {
        display: 'flex',
        gap: '10px',
    },
    discussionContent: {
        padding: '20px',
        flexGrow: 1,
        overflow: 'auto',
        textAlign: 'left',
    },
    contentText: {
        color: '#333',
        lineHeight: 1.6,
        textAlign: 'left',
    },
    repliesSection: {
        marginTop: '30px',
        textAlign: 'left',
    },
    repliesHeader: {
        fontWeight: 500,
        marginBottom: '15px',
        textAlign: 'left',
    },
    replyCard: {
        padding: '20px',
        marginBottom: '15px',
        border: '1px solid #eaeaea',
        borderRadius: '12px !important',
        textAlign: 'left',
    },
    replyAuthor: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        textAlign: 'left',
    },
    authorName: {
        marginLeft: '10px',
        fontWeight: 500,
        textAlign: 'left',
    },
    timestamp: {
        color: '#666',
        fontSize: '14px',
        marginLeft: 'auto',
    },
    footerButtons: {
        padding: '15px 20px',
        borderTop: '1px solid #eaeaea',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        backgroundColor: '#fff',
        position: 'sticky',
        bottom: 0,
    },
    cancelButton: {
        color: '#666',
        textTransform: 'none',
    },
    newDiscussionButton: {
        backgroundColor: '#1976d2',
        color: 'white',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#1565c0',
        },
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '20px',
    },
    replyEditorContainer: {
        padding: '0 20px 15px 20px',
        borderTop: '1px solid #eaeaea',
    },
    editorWrapper: {
        marginBottom: '16px',
        '& .ql-container': {
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            backgroundColor: '#fff',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: '14px',
        },
        '& .ql-toolbar': {
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            backgroundColor: '#f8f8f8',
        },
        '& .ql-editor': {
            minHeight: '120px',
            maxHeight: '250px',
            overflow: 'auto',
        }
    }
}));

// Quill modules configuration remains the same
const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link', 'image'],
        [{ 'align': [] }],
        ['clean']
    ],
    clipboard: {
        matchVisual: false,
    }
};

// Quill formats remain the same
const quillFormats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link', 'image',
    'align'
];

const DiscussionRightPanel: React.FC<DiscussionRightPanelProps> = ({
    selectedDiscussion,
    selectedDiscussionId,
    discussionId,
    onNewDiscussion,
    replyText,
    onReplyTextChange,
    onSubmitReply,
    forumId,
    onSnackbarOpen,
    onPostCountChange,
}) => {
    const classes = useStyles();
    const sharedClasses = useSharedStyles();
    const quillRef = useRef<any>(null);
    const userDetails = getUserDetails();
    console.log(forumId, "forumIdforumId")

    const [discussionPosts, setDiscussionPosts] = useState<DiscussionReply[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [discussionDetails, setDiscussionDetails] = useState<any>(null);
    const [parentPostId, setParentPostId] = useState<number | null>(null);

    // State for toggle functionalities
    const [isLocked, setIsLocked] = useState<boolean>(false);
    const [isPinned, setIsPinned] = useState<boolean>(false);
    const [isStarred, setIsStarred] = useState<boolean>(false);
    const [actionInProgress, setActionInProgress] = useState<boolean>(false);

    // Handle snackbar notifications
    const handleSnackbarOpen = (severity: 'success' | 'error', message: string) => {
        if (onSnackbarOpen) {
            onSnackbarOpen(severity, message);
        } else {
            console.log(`${severity}: ${message}`);
        }
    };

    // Fetch discussion posts
    useEffect(() => {
        console.log("discussionId:", discussionId);

        const fetchDiscussionPosts = async () => {
            if (!discussionId || !userDetails?.ContactId) {
                console.log("discussionId or userDetails.ContactId is missing");
                return;
            }
            setLoading(true);
            try {
                const payload = {
                    contactid: userDetails.ContactId,
                    discussionid: discussionId
                };

                console.log("Fetching discussion posts with payload:", payload);

                const response = await getDiscussionPosts(payload);
                console.log("Discussion posts response:", response);

                if (response?.posts && Array.isArray(response.posts)) {
                    // Format posts
                    const formattedPosts = response.posts.map((post: any) => ({
                        id: post.id || String(Math.random()),
                        userfullname: post.userfullname || "Unknown User",
                        message: post.message || "",
                        modified: post.modified || null,
                        parent: post.parent || null
                    }));

                    setDiscussionPosts(formattedPosts);

                    if (onPostCountChange) {
                        onPostCountChange(response.posts.length);
                    }

                    // If the first post is the main discussion post
                    if (response.posts.length > 0) {
                        const mainPost = response.posts.find((post: any) => !post.parent);
                        console.log("Main post found:", mainPost);
                        if (mainPost) {
                            setDiscussionDetails({
                                title: mainPost.subject || "Discussion",
                                id: mainPost.id || null,
                                locked: response.locked || false,
                                pinned: response.pinned || false,
                                starred: response.starred || false
                            });

                            // Set toggle states
                            setIsLocked(response.locked || false);
                            setIsPinned(response.pinned || false);
                            setIsStarred(response.starred || false);

                            // Use mainPost.id for parent post ID
                            setParentPostId(mainPost.id || null);
                            console.log("Parent Post ID set to:", mainPost.id);
                        }
                    }
                } else {
                    console.log("No posts found in response");
                    setDiscussionPosts([]);
                }
            } catch (error) {
                console.error("Error fetching discussion posts:", error);
                setDiscussionPosts([]);
                handleSnackbarOpen('error', 'Failed to load discussion posts');
            } finally {
                setLoading(false);
            }
        };

        fetchDiscussionPosts();
    }, [discussionId, userDetails?.ContactId]);

    const handleEditorChange = (content: string) => {
        onReplyTextChange(content);
    };

    // Handle submit reply using addForumDiscussionPost API
    const handleSubmitReply = async () => {
        if (!replyText.trim()) {
            console.log("Reply text is empty");
            return;
        }

        if (!userDetails?.ContactId) {
            console.log("User contact ID is missing");
            return;
        }

        if (!parentPostId) {
            console.log("Parent post ID is missing:", parentPostId);
            return;
        }

        setSubmitting(true);
        try {
            // Prepare payload for addForumDiscussionPost
            const payload = {
                contactid: userDetails.ContactId,
                postid: parentPostId,
                subject: discussionDetails?.title || selectedDiscussion?.title || "Discussion",
                message: replyText
            };

            console.log("Submitting reply with payload:", payload);

            // Call the addForumDiscussionPost API
            const response = await addForumDiscussionPost(payload);
            console.log("Reply submission response:", response);

            // Clear the editor after successful submission
            onReplyTextChange("");
            handleSnackbarOpen('success', 'Reply posted successfully');

            // Refresh the discussion posts
            refreshDiscussionPosts();

            // Call the parent component's onSubmitReply function
            onSubmitReply();

        } catch (error) {
            console.error("Error submitting reply:", error);
            handleSnackbarOpen('error', 'Failed to post reply');
        } finally {
            setSubmitting(false);
        }
    };

    // Function to refresh discussion posts
    const refreshDiscussionPosts = async () => {
        if (!discussionId || !userDetails?.ContactId) return;

        try {
            const refreshPayload = {
                contactid: userDetails.ContactId,
                discussionid: discussionId
            };

            const refreshResponse = await getDiscussionPosts(refreshPayload);

            if (refreshResponse?.posts && Array.isArray(refreshResponse.posts)) {
                const formattedPosts = refreshResponse.posts.map((post: any) => ({
                    id: post.id || String(Math.random()),
                    userfullname: post.userfullname || "Unknown User",
                    message: post.message || "",
                    modified: post.modified || null,
                    parent: post.parent || null
                }));

                setDiscussionPosts(formattedPosts);

                // Update toggle states if available in response
                if (refreshResponse.hasOwnProperty('locked')) {
                    setIsLocked(refreshResponse.locked);
                }
                if (refreshResponse.hasOwnProperty('pinned')) {
                    setIsPinned(refreshResponse.pinned);
                }
                if (refreshResponse.hasOwnProperty('starred')) {
                    setIsStarred(refreshResponse.starred);
                }
            }
        } catch (error) {
            console.error("Error refreshing discussion posts:", error);
        }
    };

    // Handle lock toggle discussion
    const handleLockToggle = async () => {
        console.log("Lock toggle clicked");
        console.log("discussionId:", discussionId);
        console.log("forumId:", forumId);
        console.log("actionInProgress:", actionInProgress);
        if (!discussionId || !forumId || actionInProgress) return;

        setActionInProgress(true);
        try {
            const targetState = isLocked ? 0 : 1;
            const payload = {
                forumid: forumId,
                discussionid: discussionId,
                targetstate: targetState,
                method: 'post'
            };

            console.log("Lock toggle payload:", payload);

            const response = await lockToggleDiscussion(payload);
            console.log("Lock toggle response:", response);

            setIsLocked(!isLocked);
            handleSnackbarOpen('success', `Discussion ${isLocked ? 'unlocked' : 'locked'} successfully`);

            // Refresh discussion to update UI
            refreshDiscussionPosts();

        } catch (error) {
            console.error("Error toggling discussion lock:", error);
            handleSnackbarOpen('error', 'Failed to toggle discussion lock');
        } finally {
            setActionInProgress(false);
        }
    };

    // Handle pin toggle discussion
    const handlePinToggle = async () => {
        if (!discussionId || actionInProgress) return;

        setActionInProgress(true);
        try {
            const targetState = isPinned ? 0 : 1;
            const payload = {
                discussionid: discussionId,
                targetstate: targetState,
                method: 'post'
            };

            console.log("Pin toggle payload:", payload);

            const response = await pinToggleDiscussion(payload);
            console.log("Pin toggle response:", response);

            setIsPinned(!isPinned);
            handleSnackbarOpen('success', `Discussion ${isPinned ? 'unpinned' : 'pinned'} successfully`);

            // Refresh discussion to update UI
            refreshDiscussionPosts();

        } catch (error) {
            console.error("Error toggling discussion pin:", error);
            handleSnackbarOpen('error', 'Failed to toggle discussion pin');
        } finally {
            setActionInProgress(false);
        }
    };

    // Handle star toggle discussion
    const handleStarToggle = async () => {
        if (!discussionId || !userDetails?.ContactId || actionInProgress) return;

        setActionInProgress(true);
        try {
            const targetState = isStarred ? 0 : 1;
            const payload = {
                contactid: userDetails.ContactId,
                discussionid: discussionId,
                targetstate: targetState,
                method: 'post'
            };

            console.log("Star toggle payload:", payload);

            const response = await starToggleDiscussion(payload);
            console.log("Star toggle response:", response);

            setIsStarred(!isStarred);
            handleSnackbarOpen('success', `Discussion ${isStarred ? 'unstarred' : 'starred'} successfully`);

            // Refresh discussion to update UI
            refreshDiscussionPosts();

        } catch (error) {
            console.error("Error toggling discussion star:", error);
            handleSnackbarOpen('error', 'Failed to toggle discussion star');
        } finally {
            setActionInProgress(false);
        }
    };

    // Improved getRelativeTime function for WhatsApp-like timestamps
    const getRelativeTime = (timestamp: number) => {
        if (!timestamp) return "Unknown time";

        const now = dayjs();
        const postTime = dayjs.unix(timestamp);
        const diffMinutes = now.diff(postTime, 'minute');

        if (diffMinutes < 1) return "Just now";
        if (diffMinutes < 60) return `${diffMinutes} min ago`;

        const diffHours = now.diff(postTime, 'hour');
        if (diffHours < 24) return `${diffHours} hr ago`;

        const diffDays = now.diff(postTime, 'day');
        if (diffDays < 7) return `${diffDays} days ago`;

        return postTime.format("DD MMM YYYY");
    };

    return (
        <Box className={classes.rightPanel}>
            {selectedDiscussion || discussionDetails ? (
                <>
                    <Box className={classes.discussionHeader}>
                        <Typography className={`${classes.discussionTitle} ${sharedClasses.font16500}`} align="left">
                            {discussionDetails?.title || selectedDiscussion?.title || "Discussion"}
                            <Chip label="Active" className={classes.activeChip} sx={{ background: "#C1DCC3" }} />
                        </Typography>
                        <Box className={classes.iconButtonGroup}>
                            <IconButton
                                size="small"
                                onClick={handleStarToggle}
                                disabled={actionInProgress}
                            >
                                {isStarred ? <StarIcon color="primary" /> : <StarBorderIcon />}
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleLockToggle}
                                disabled={actionInProgress}
                            >
                                {isLocked ? <LockIcon color="primary" /> : <LockOpenIcon />}
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handlePinToggle}
                                disabled={actionInProgress}
                            >
                                {isPinned ? <PushPinIcon color="primary" /> : <PushPinIcon />}
                            </IconButton>
                        </Box>
                    </Box>

                    <Box className={classes.discussionContent}>
                        {/* Initial discussion content - if needed */}
                        {selectedDiscussion && (
                            <Paper className={classes.replyCard} elevation={0}>
                                <Box className={classes.replyAuthor}>
                                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#e0e0e0' }}>
                                        {selectedDiscussion.userfullname?.charAt(0) || "U"}
                                    </Avatar>
                                    <Typography className={classes.authorName}>
                                        {selectedDiscussion.userfullname || "Unknown User"}
                                    </Typography>
                                    <Typography className={classes.timestamp}>
                                        {getRelativeTime(selectedDiscussion.modified)}
                                    </Typography>
                                </Box>
                                <div
                                    className={sharedClasses.font14400}
                                    dangerouslySetInnerHTML={{ __html: selectedDiscussion.message || "" }}
                                />
                            </Paper>
                        )}

                        <Box className={classes.repliesSection}>
                            <Typography className={`${classes.repliesHeader} ${sharedClasses.font16500}`} align="left">
                                All replies ({discussionPosts.length})
                            </Typography>

                            {loading ? (
                                <Typography variant="body2" color="textSecondary">Loading replies...</Typography>
                            ) : discussionPosts.length > 0 ? (
                                // Display posts in reverse order (newest at the bottom like WhatsApp)
                                [...discussionPosts].reverse().map((reply) => (
                                    <Paper key={reply.id} className={classes.replyCard} elevation={0}>
                                        <div
                                            className={sharedClasses.font14400}
                                            dangerouslySetInnerHTML={{ __html: reply.message || "" }}
                                        />
                                        <Box className={classes.replyAuthor}>
                                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#e0e0e0' }}>
                                                {reply.userfullname?.charAt(0) || "U"}
                                            </Avatar>
                                            <Typography className={classes.authorName}>
                                                {reply.userfullname || "Unknown User"}
                                            </Typography>
                                            <Typography className={classes.timestamp}>
                                                {getRelativeTime(reply.modified)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary">No replies yet</Typography>
                            )}
                        </Box>
                    </Box>

                    {/* React-Quill Editor for replies */}
                    <Box className={classes.replyEditorContainer}>
                        <Box className={classes.editorWrapper}>
                            <ReactQuill
                                ref={quillRef}
                                value={replyText}
                                onChange={handleEditorChange}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Enter your reply..."
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmitReply}
                                disabled={!replyText.trim() || submitting || isLocked}
                            >
                                {submitting ? "Submitting..." : "Submit Reply"}
                            </Button>
                        </Box>
                    </Box>
                </>
            ) : (
                <Box className={classes.emptyState}>
                    <Typography variant="body1" color="textSecondary" gutterBottom align="left">
                        No discussion selected
                    </Typography>
                    <Button
                        variant="contained"
                        className={classes.newDiscussionButton}
                        onClick={onNewDiscussion}
                    >
                        New discussion
                    </Button>
                </Box>
            )}

            <Box className={classes.footerButtons}>
                <Button
                    variant="outlined"
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    className={classes.newDiscussionButton}
                    onClick={onNewDiscussion}
                >
                    New discussion
                </Button>
            </Box>
        </Box>
    );
};

export default DiscussionRightPanel;